<template>
  <div class="ace-grain-beans">
    <Steps class="ace-grain-beans__steps" :steps="steps" :currentStep="currentStep" />
    <Step1
      v-if="currentStep === 1"
      :feedMaterials="feedMaterials"
      @setFeedMaterial="setFeedMaterial"
    />
    <Step2
      v-if="currentStep === 2"
      :feedMaterial="feedMaterialData"
      :fuel="fuel"
      :efBreakdown="efBreakdown"
      :resultsToShow="resultsToShow"
      @setFuel="setFuel"
      @nextStep="incrementStep"
    />
    <Step3
      v-if="currentStep === 3"
      :feedMaterial="feedMaterialData"
      :fuel="fuel"
      :fuelName="fuelName"
      :efBreakdown="efBreakdown"
      :resultsToShow="resultsToShow"
      :chartData="chartData"
    />
  </div>
</template>

<script>
/* eslint-disable */

import { round } from '../../helpers';

import steps from './_steps';
import feedMaterials from './_feedMaterials';
import treatments from './_treatments';
import grainApplicationRates from './_grainApplicationRates';
import nutritionalLossesBaseline from './_nutritionalLossesBaseline';
import mceslFootprint from './_mceslFootprint';
import fuelOptions from './_fuelOptions';

import Steps from '../Layout/Steps.vue';
import Step1 from './Step-1.vue';
import Step2 from './Step-2.vue';
import Step3 from './Step-3.vue';

import './images/barley.png';
import './images/barley@2x.png';
import './images/beans.png';
import './images/beans@2x.png';
import './images/wheat.png';
import './images/wheat@2x.png';

export default {
  data() {
    return {
      steps,
      feedMaterials,
      treatments,
      grainApplicationRates,
      nutritionalLossesBaseline,
      mceslFootprint,
      currentStep: 1,
      fuel: 'kerosene',
      fuelOptions,
      feedMaterial: '',
    };
  },
  props: [],
  methods: {
    setFeedMaterial(feedMaterial) {
      this.feedMaterial = feedMaterial;

      this.incrementStep();
    },
    setFuel(fuel) {
      this.fuel = fuel;
    },
    incrementStep() {
      window.scrollTo(0, 0);
      this.currentStep += 1;
    },
  },
  computed: {
    fuelName() {
      return this.fuelOptions[this.fuel] || '';
    },
    feedMaterialData() {
      return this.feedMaterials[this.feedMaterial] || {};
    },
    applicableRate() {
      return (
        this.grainApplicationRates[this.feedMaterial][
          Object.keys(this.grainApplicationRates[this.feedMaterial])
            .map(k => parseInt(k, 10))
            .sort((a, b) => b - a)
            .find(rate => this.feedMaterialData.moisture >= rate)
        ] || null
      );
    },
    applicableBaseline() {
      return this.nutritionalLossesBaseline[this.feedMaterial][this.fuel];
    },
    efBreakdown() {
      const values = {
        ...this.treatments,
      };
      const { weight, moisture } = this.feedMaterialData;
      const rate = this.applicableRate;

      Object.keys(values).forEach(key => {
        const name = values[key];
        let electricalDryer = null;
        let electricalAeration = null;
        let thermalDryer = null;
        let nutritionalLosses = null;
        let mycocurb = null;

        if (['kerosene', 'gas', 'naturalGas', 'lpg'].includes(key)) {
          const {
            baselineEndMC,
            moistureChange,
            baselineThermalDryer,
            baselineElectricalDryer,
          } = this.nutritionalLossesBaseline[this.feedMaterial][key];

          electricalDryer = round(
            baselineElectricalDryer * ((moisture - baselineEndMC) / moistureChange),
            1
          );
          thermalDryer = round(
            baselineThermalDryer * ((moisture - baselineEndMC) / moistureChange),
            1
          );
          electricalAeration = 4.9;
          nutritionalLosses = 17;
        }

        if (key === 'aerated') {
          electricalAeration = 3.9;
          nutritionalLosses = 25;
        }

        if (key === 'adesco') {
          electricalAeration = 3.2;
          nutritionalLosses = 1.5;
          mycocurb = round(rate * this.mceslFootprint, 1);
        }

        const totalEfPerTonne = round(
          electricalDryer + electricalAeration + thermalDryer + nutritionalLosses + mycocurb,
          1
        );
        const annualFootprint = round((totalEfPerTonne * weight) / 1000, 1);

        values[key] = {
          name,
          electricalDryer,
          thermalDryer,
          mycocurb,
          electricalAeration,
          nutritionalLosses,
          totalEfPerTonne,
          annualFootprint,
        };
      });

      return values;
    },
    resultsToShow() {
      return [
        this.fuel,
        ...(this.feedMaterialData.moisture <= 17 && this.feedMaterial !== 'beans'
          ? ['aerated']
          : []),
        ...['adesco'],
      ];
    },
    chartSeries() {
      return this.resultsToShow;
    },
    chartLabels() {
      return this.chartSeries.map(treatment => this.treatments[treatment]);
    },
    chartData() {
      const datasets = [
        {
          label: 'Nutritional Losses',
          backgroundColor: '#02A94E',
          data: this.chartSeries.map(series => this.efBreakdown[series].nutritionalLosses),
        },
        {
          label: 'Electrical Energy - Aeration',
          backgroundColor: '#37CE2A',
          data: this.chartSeries.map(series => this.efBreakdown[series].electricalAeration),
        },
        {
          label: 'Thermal Energy - Dryer',
          backgroundColor: '#76DE6D',
          data: this.chartSeries.map(series => this.efBreakdown[series].thermalDryer),
        },
        {
          label: 'Electrical Energy - Dryer',
          backgroundColor: '#99E693',
          data: this.chartSeries.map(series => this.efBreakdown[series].electricalDryer),
        },
        {
          label: 'Mycocurb',
          backgroundColor: '#B7F8B2',
          data: this.chartSeries.map(series => this.efBreakdown[series].mycocurb),
        },
      ];
      return {
        labels: this.chartLabels,
        datasets,
        totals: this.chartSeries.map((series, index) => this.efBreakdown[series].totalEfPerTonne),
      };
    },
  },
  components: {
    Steps,
    Step1,
    Step2,
    Step3,
  },
};
</script>

<style scoped lang="scss">
.ace-grain-beans {
  display: flex;
  flex-direction: column;

  align-items: center;
}
</style>
